// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Form,
    FormField,
    Section,
    Translate,
} from '@plesk/ui-library';
import {
    intMinRule,
    validate,
} from 'common/validator';
import {
    IBackupResponse,
    IRestoreServerRequest,
} from 'common/api/resources/Backup';
import { RootState } from 'admin/core/store';
import { nestStringProperties } from 'common/modules/app/formErrors/selectors';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import { connect } from 'react-redux';
import * as backupActions from 'common/modules/backup/actions';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { Button } from 'admin/common/components/Button/Button';
import AsyncSelectInput from 'common/components/Select/AsyncSelectInput';
import { createOptionsLoader } from 'common/components/Select/helpers';
import {
    IUserResponse,
    users,
} from 'common/api/resources/User';
import {
    createUserProjectsOptionsLoader,
    useUserDefaultProjectLoader,
} from 'admin/user/helpers/projects';
import { osImages } from 'common/api/resources/OsImage';
import {
    computeResources,
    IComputeResourceResponse,
} from 'common/api/resources/ComputeResource';
import { hasPermission } from 'common/modules/permission/selectors';
import { PERMISSION_LIST } from 'common/modules/permission/constants';
import { ISelectOption } from 'common/components';

interface IRestoreFormProps {
    backup?: IBackupResponse;
    onSubmit: () => void;
}

export type RestoreFormProps =
    IRestoreFormProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const RestoreForm: React.FC<RestoreFormProps> = ({
    backup,
    onSubmit,
    formErrors,
    permissions: {
        canGetComputeResources,
        canGetOsImages,
        canGetUsers,
    },
    formErrorsActions: {
        setFormErrors,
        clearFormErrors,
    },
    backupActions: {
        restoreDeletedServerFromBackup,
    },
}) => {
    const [submitValues, setSubmitValues] = React.useState<IRestoreServerRequest>({
        destination_compute_resource_id: backup?.compute_resource_vm?.compute_resource?.id ?? 0,
        temporary_os_image_version_id: 0,
        user_id: backup?.compute_resource_vm?.user?.id ?? 0,
        project_id: backup?.compute_resource_vm?.project?.id ?? 0,
    });
    const {
        selectedProjectOption,
        setSelectedProjectOption,
        isLoadingDefaultProject,
        loadDefaultProject,
    } = useUserDefaultProjectLoader<ISelectOption>(
        backup?.compute_resource_vm?.project && {
            label: backup.compute_resource_vm.project.name,
            value: backup.compute_resource_vm.project.id,
        }
    );

    React.useEffect(() => {
        clearFormErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (backup === undefined) {
        onSubmit();
        return null;
    }

    const handleSubmit = async (values: IRestoreServerRequest) => {
        const errors = validate<IRestoreServerRequest>(values, {
            destination_compute_resource_id: intMinRule(<Translate content="validate.fieldRequired" />, 1),
            temporary_os_image_version_id: intMinRule(<Translate content="validate.fieldRequired" />, 1),
            user_id: intMinRule(<Translate content="validate.fieldRequired" />, 1),
            project_id: intMinRule(<Translate content="validate.fieldRequired" />, 1),
        });

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        restoreDeletedServerFromBackup(backup.id, values);
        onSubmit();
    };

    const sourceGpuProfileId = backup.compute_resource_vm?.gpu_profile?.id;
    const sourceGpuModelId = backup.compute_resource_vm?.gpu_model?.id;
    const loadComputeResourceOptions = createOptionsLoader(
        computeResources.list,
        (cr: IComputeResourceResponse): ISelectOption => ({
            label: cr.name,
            value: cr.id,
        }),
        canGetComputeResources,
        sourceGpuProfileId || sourceGpuModelId ? {
            ...(sourceGpuProfileId ? { with_available_gpu_profile: sourceGpuProfileId } : {}),
            ...(sourceGpuModelId ? { with_available_gpu_model: sourceGpuModelId } : {}),
        } : undefined
    );

    const loadOsImagesOptions = createOptionsLoader(
        osImages.list,
        (image) => ({
            label: image.name,
            options: image.versions.map((version) => ({
                label: version.version,
                value: version.id,
            })),
        }),
        canGetOsImages,
        {
            virtualization_type: backup.compute_resource_vm?.virtualization_type,
        }
    );

    const loadUserOptions = createOptionsLoader(
        users.list,
        (item: IUserResponse): ISelectOption => ({
            label: item.email,
            value: item.id,
        }),
        canGetUsers
    );

    const loadProjectOptions = createUserProjectsOptionsLoader(
        submitValues.user_id,
        { owner_id: submitValues.user_id }
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleComputeResourceChange = (option: any) => {
        setSubmitValues(values => ({
            ...values,
            destination_compute_resource_id: option.value,
        }));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleOsImageChange = (option: any) => {
        setSubmitValues(values => ({
            ...values,
            temporary_os_image_version_id: option.value,
        }));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleUserChange = (option: any) => {
        const userId = option.value;

        setSubmitValues(values => ({
            ...values,
            user_id: userId,
            project_id: 0,
        }));
        setSelectedProjectOption(undefined);

        loadDefaultProject(userId, { owner_id: userId }, (defaultOption) => {
            setSubmitValues(values => ({
                ...values,
                project_id: defaultOption.value,
            }));
        });
    };

    const handleProjectChange = (option: ISelectOption) => {
        setSubmitValues(values => ({
            ...values,
            project_id: option.value as number,
        }));
        setSelectedProjectOption(option);
    };

    return (
        <>
            <Form
                id="restoreForm"
                onSubmit={handleSubmit}
                values={submitValues}
                footerClassName="hidden"
                errors={formErrors}
                hideRequiredLegend={true}
                submitButton={false}
                cancelButton={false}
                applyButton={false}
                vertical={true}
            >
                <Section>
                    <FormField
                        name="destination_compute_resource_id"
                        label={<Translate content="backups.restoreForm.destinationComputeResource" />}
                        required={true}
                    >
                        {({ getId }) => (
                            <AsyncSelectInput
                                inputId={getId()}
                                defaultValue={{
                                    value: backup.compute_resource_vm?.compute_resource?.id,
                                    label: backup.compute_resource_vm?.compute_resource?.name,
                                } as ISelectOption}
                                loadOptions={loadComputeResourceOptions}
                                onChange={handleComputeResourceChange}
                                debounceTimeout={1000}
                                additional={{ page: 1 }}
                                menuPosition="fixed"
                            />
                        )}
                    </FormField>
                    <FormField
                        name="temporary_os_image_version_id"
                        label={<Translate content="backups.restoreForm.temporaryOsImageVersion" />}
                        required={true}
                    >
                        {({ getId }) => (
                            <AsyncSelectInput
                                inputId={getId()}
                                menuPosition="fixed"
                                loadOptions={loadOsImagesOptions}
                                onChange={handleOsImageChange}
                                debounceTimeout={1000}
                                additional={{ page: 1 }}
                            />
                        )}
                    </FormField>
                    <FormField
                        name="user_id"
                        label={<Translate content="backups.restoreForm.user" />}
                        required={true}
                    >
                        {({ getId }) => (
                            <AsyncSelectInput
                                inputId={getId()}
                                menuPosition="fixed"
                                loadOptions={loadUserOptions}
                                onChange={handleUserChange}
                                debounceTimeout={1000}
                                additional={{ page: 1 }}
                                defaultValue={{
                                    value: backup.compute_resource_vm?.user?.id,
                                    label: backup.compute_resource_vm?.user?.email,
                                } as ISelectOption}
                            />
                        )}
                    </FormField>
                    <FormField
                        name="project_id"
                        label={<Translate content="backups.restoreForm.project" />}
                        required={true}
                    >
                        {({ getId }) => (
                            <AsyncSelectInput
                                inputId={getId()}
                                menuPosition="fixed"
                                loadOptions={loadProjectOptions}
                                onChange={handleProjectChange}
                                debounceTimeout={1000}
                                additional={{ page: 1 }}
                                cacheUniqs={[submitValues.user_id]}
                                value={selectedProjectOption ?? null}
                                isLoading={isLoadingDefaultProject || undefined}
                                isDisabled={submitValues.user_id <= 0}
                            />
                        )}
                    </FormField>
                </Section>
            </Form>
            <Button
                type="submit"
                form="restoreForm"
                fill={true}
                intent={INTENT_TYPE.PRIMARY}
                size={SIZE.LG}
            >
                <Translate content="backups.restoreForm.button" />
            </Button>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    formErrors: nestStringProperties(state),
    permissions: {
        canGetComputeResources: hasPermission(state, PERMISSION_LIST.MANAGE_COMPUTE_RESOURCES, PERMISSION_LIST.GET_COMPUTE_RESOURCES),
        canGetOsImages: hasPermission(state, PERMISSION_LIST.MANAGE_OS_IMAGES, PERMISSION_LIST.GET_OS_IMAGES),
        canGetUsers: hasPermission(state, PERMISSION_LIST.MANAGE_USERS, PERMISSION_LIST.GET_USERS),
    },
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    backupActions: bindActionCreators(backupActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(RestoreForm);